import { useCallback, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import type { RecipeCustomisations, RecipeQueryResponse } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useRecipeDetailData } from 'routes/Menu/components/Detail/hooks/useRecipeDetailData'
import { getBasketDate, getNumPortions } from 'selectors/basket'

import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'

type IngredientCustomisations = Extract<RecipeCustomisations, { type: 'ingredients' }>

export type RecipeGridCustomisationData = {
  customisations?: IngredientCustomisations
  customisationRecipe?: RecipeQueryResponse
  isLoadingCustomisations: boolean
  hasCustomisationError: boolean
  hasOpenedCustomisations: boolean
}

type UseRecipeGridCustomisationDataArgs = {
  collectionId?: string
}

type TrackRecipeAlternativeOptionsMenuOpen = {
  action: 'recipe-alternative-options-menu-open'
  recipeId: RecipeUUID
  collectionId: string
}

type TrackClickCustomise = {
  action: 'click_customise'
  properties: {
    collection_id: string
    recipe_id: RecipeUUID
    recipe_uuid: RecipeUUID
    menu_uuid: string | null
    event_screen: 'view_recipe_list_screen'
    recipe_list_position?: number
  }
}

const idleCustomisationData: RecipeGridCustomisationData = {
  isLoadingCustomisations: false,
  hasCustomisationError: false,
  hasOpenedCustomisations: false,
}

const hasIngredientCustomisationOptions = (customisations: IngredientCustomisations) =>
  customisations.slots.some((slot) => slot.values.length > 1)

const mapRecipeCustomisationState = (
  loading: boolean,
  recipe: RecipeQueryResponse | null,
  error: boolean,
  hasOpenedCustomisations: boolean,
): RecipeGridCustomisationData => {
  if (!hasOpenedCustomisations) {
    return idleCustomisationData
  }

  if (loading) {
    return {
      isLoadingCustomisations: true,
      hasCustomisationError: false,
      hasOpenedCustomisations: true,
    }
  }

  if (error) {
    return {
      isLoadingCustomisations: false,
      hasCustomisationError: true,
      hasOpenedCustomisations: true,
    }
  }

  if (
    !recipe ||
    recipe.customisations.type !== 'ingredients' ||
    !hasIngredientCustomisationOptions(recipe.customisations)
  ) {
    return {
      isLoadingCustomisations: false,
      hasCustomisationError: false,
      hasOpenedCustomisations: true,
    }
  }

  return {
    isLoadingCustomisations: false,
    hasCustomisationError: false,
    hasOpenedCustomisations: true,
    customisationRecipe: recipe,
    customisations: recipe.customisations,
  }
}

export const useRecipeGridCustomisationData = ({
  collectionId,
}: UseRecipeGridCustomisationDataArgs) => {
  const [activeRecipeId, setActiveRecipeId] = useState<RecipeUUID | null>(null)
  const { menuId } = useMenuExperienceRecipeDetails()
  const deliveryDate = useSelector(getBasketDate)
  const numPortions = useSelector(getNumPortions)
  const shouldFetchOptions = Boolean(activeRecipeId && deliveryDate && numPortions)

  const { loading, recipe, error } = useRecipeDetailData({
    menuId,
    recipeUuid: activeRecipeId,
    numPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
  })

  const activeRecipeState = useMemo(
    () => mapRecipeCustomisationState(loading, recipe, error, Boolean(activeRecipeId)),
    [activeRecipeId, error, loading, recipe],
  )

  const getRecipeCustomisationData = useCallback(
    (recipeId: RecipeUUID): RecipeGridCustomisationData =>
      recipeId === activeRecipeId ? activeRecipeState : idleCustomisationData,
    [activeRecipeId, activeRecipeState],
  )

  const openRecipeCustomisations = useCallback(
    (recipeId: RecipeUUID, recipePosition?: number) => {
      setActiveRecipeId(recipeId)

      if (!collectionId) {
        return
      }

      queueTrackingEvent<TrackRecipeAlternativeOptionsMenuOpen>({
        action: 'recipe-alternative-options-menu-open',
        recipeId,
        collectionId,
      })

      queueTrackingEvent<TrackClickCustomise>({
        action: 'click_customise',
        properties: {
          collection_id: collectionId,
          recipe_id: recipeId,
          recipe_uuid: recipeId,
          menu_uuid: menuId,
          event_screen: 'view_recipe_list_screen',
          recipe_list_position: recipePosition,
        },
      })
    },
    [collectionId, menuId],
  )

  return {
    activeRecipeId,
    openRecipeCustomisations,
    closeRecipeCustomisations: () => setActiveRecipeId(null),
    getRecipeCustomisationData,
  }
}
